import { useState, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateBookingServiceInfo } from '@/api/generated/sdk.gen';
import type { BookingServiceInfo, UpdateBookingServiceInfoRequest } from '@/api/generated/types.gen';
import { bookingQuery } from '@/lib/bookings';
import { errorMessage } from '@/lib/errors';
import { zonedToUTC, utcToZonedInput } from '@/lib/tz';
import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';

// rawBody is a thin cast helper: @hey-ai/openapi-ts drops the null arm from
// nullable optional fields, but the backend PATCH /bookings/{id}/service-info
// accepts explicit null to clear a column (expected_checkin_at, etc.).
// Cast through this so the wire payload preserves explicit nulls.
function rawBody<T extends UpdateBookingServiceInfoRequest>(b: Record<string, unknown>): T {
  return b as unknown as T;
}

interface Props {
  bookingId: string;
  tz: string;
  serviceInfo?: BookingServiceInfo;
}

interface Draft {
  checkin: string;      // datetime-local value in tz, or ''
  checkout: string;     // datetime-local value in tz, or ''
  crib: number;
  highChair: number;
  extraBed: number;
  extraLinen: number;
  parkingNeeded: boolean;
  parkingNote: string;
  pets: boolean;
  earlyCheckin: boolean;
  lateCheckout: boolean;
  notes: string;
}

function draftFromServiceInfo(si: BookingServiceInfo | undefined, tz: string): Draft {
  return {
    checkin: si?.expected_checkin_at ? utcToZonedInput(si.expected_checkin_at, tz) : '',
    checkout: si?.expected_checkout_at ? utcToZonedInput(si.expected_checkout_at, tz) : '',
    crib: si?.crib ?? 0,
    highChair: si?.high_chair ?? 0,
    extraBed: si?.extra_bed ?? 0,
    extraLinen: si?.extra_linen ?? 0,
    parkingNeeded: si?.parking_needed ?? false,
    parkingNote: si?.parking_note ?? '',
    pets: si?.pets ?? false,
    earlyCheckin: si?.early_checkin ?? false,
    lateCheckout: si?.late_checkout ?? false,
    notes: si?.notes ?? '',
  };
}

function draftsEqual(a: Draft, b: Draft): boolean {
  return (
    a.checkin === b.checkin &&
    a.checkout === b.checkout &&
    a.crib === b.crib &&
    a.highChair === b.highChair &&
    a.extraBed === b.extraBed &&
    a.extraLinen === b.extraLinen &&
    a.parkingNeeded === b.parkingNeeded &&
    a.parkingNote === b.parkingNote &&
    a.pets === b.pets &&
    a.earlyCheckin === b.earlyCheckin &&
    a.lateCheckout === b.lateCheckout &&
    a.notes === b.notes
  );
}

// ─── Stepper ──────────────────────────────────────────────────────────────────

interface StepperProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
}

function Stepper({ label, value, onChange, min = 0 }: StepperProps) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-28 text-sm text-foreground">{label}</span>
      <button
        type="button"
        aria-label={`${label} minus`}
        className="flex h-7 w-7 items-center justify-center rounded border border-[var(--border-hairline)] bg-card text-sm hover:bg-[var(--surface-sunken)] disabled:opacity-40"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
      >
        −
      </button>
      <span className="w-6 text-center text-sm tabular-nums">{value}</span>
      <button
        type="button"
        aria-label={`${label} plus`}
        className="flex h-7 w-7 items-center justify-center rounded border border-[var(--border-hairline)] bg-card text-sm hover:bg-[var(--surface-sunken)]"
        onClick={() => onChange(value + 1)}
      >
        +
      </button>
    </div>
  );
}

// ─── Toggle row ───────────────────────────────────────────────────────────────

interface ToggleRowProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}

function ToggleRow({ id, label, checked, onChange }: ToggleRowProps) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-[var(--border-hairline)]"
      />
      {label}
    </label>
  );
}

// ─── ServiceInfoEditor ────────────────────────────────────────────────────────

export function ServiceInfoEditor({ bookingId, tz, serviceInfo }: Props) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const initial = useMemo(() => draftFromServiceInfo(serviceInfo, tz), [serviceInfo, tz]);
  const [draft, setDraft] = useState<Draft>(initial);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const isDirty = !draftsEqual(draft, initial);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const save = useMutation({
    mutationFn: () => {
      const body = {
        crib: draft.crib,
        high_chair: draft.highChair,
        extra_bed: draft.extraBed,
        extra_linen: draft.extraLinen,
        parking_needed: draft.parkingNeeded,
        parking_note: draft.parkingNeeded ? (draft.parkingNote || null) : null,
        pets: draft.pets,
        early_checkin: draft.earlyCheckin,
        late_checkout: draft.lateCheckout,
        notes: draft.notes || null,
        expected_checkin_at: draft.checkin ? zonedToUTC(draft.checkin, tz) : null,
        expected_checkout_at: draft.checkout ? zonedToUTC(draft.checkout, tz) : null,
      };
      return updateBookingServiceInfo({
        throwOnError: true,
        path: { id: bookingId },
        body: rawBody(body),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bookingQuery(bookingId).queryKey });
      toast({ kind: 'success', title: 'Service info saved' });
      setSaveErr(null);
    },
    onError: (e) => setSaveErr(errorMessage(e)),
  });

  // Tz display name (e.g. "Europe/Belgrade")
  const tzLabel = tz;

  return (
    <div className="flex flex-col gap-6">
      {saveErr && (
        <p role="alert" className="rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-3 py-2 text-sm text-destructive">
          {saveErr}
        </p>
      )}

      {/* Expected times */}
      <section>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
          Expected times
          <span className="ml-1 font-normal normal-case tracking-normal text-muted">({tzLabel})</span>
        </h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="si-checkin"
              className="mb-1 block text-xs text-muted"
            >
              Expected check-in
            </label>
            <input
              id="si-checkin"
              type="datetime-local"
              value={draft.checkin}
              onChange={(e) => set('checkin', e.target.value)}
              className="w-full rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-card px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--ollio-primary)]"
            />
          </div>
          <div>
            <label
              htmlFor="si-checkout"
              className="mb-1 block text-xs text-muted"
            >
              Expected check-out
            </label>
            <input
              id="si-checkout"
              type="datetime-local"
              value={draft.checkout}
              onChange={(e) => set('checkout', e.target.value)}
              className="w-full rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-card px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--ollio-primary)]"
            />
          </div>
        </div>
      </section>

      {/* Counters */}
      <section>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
          Items requested
        </h3>
        <div className="flex flex-col gap-2">
          <Stepper label="Crib" value={draft.crib} onChange={(v) => set('crib', v)} />
          <Stepper label="High chair" value={draft.highChair} onChange={(v) => set('highChair', v)} />
          <Stepper label="Extra bed" value={draft.extraBed} onChange={(v) => set('extraBed', v)} />
          <Stepper label="Extra linen" value={draft.extraLinen} onChange={(v) => set('extraLinen', v)} />
        </div>
      </section>

      {/* Toggles */}
      <section>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
          Special requests
        </h3>
        <div className="flex flex-col gap-2">
          <ToggleRow
            id="si-early-checkin"
            label="Early check-in"
            checked={draft.earlyCheckin}
            onChange={(v) => set('earlyCheckin', v)}
          />
          <ToggleRow
            id="si-late-checkout"
            label="Late check-out"
            checked={draft.lateCheckout}
            onChange={(v) => set('lateCheckout', v)}
          />
          <ToggleRow
            id="si-pets"
            label="Pets"
            checked={draft.pets}
            onChange={(v) => set('pets', v)}
          />
          <ToggleRow
            id="si-parking"
            label="Parking needed"
            checked={draft.parkingNeeded}
            onChange={(v) => set('parkingNeeded', v)}
          />
          {draft.parkingNeeded && (
            <div className="ml-6">
              <label htmlFor="si-parking-note" className="mb-1 block text-xs text-muted">
                Parking note
              </label>
              <input
                id="si-parking-note"
                type="text"
                value={draft.parkingNote}
                onChange={(e) => set('parkingNote', e.target.value)}
                placeholder="e.g. licence plate, spot number…"
                className="w-full rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-card px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--ollio-primary)]"
              />
            </div>
          )}
        </div>
      </section>

      {/* Internal notes */}
      <section>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
          Internal notes
        </h3>
        <textarea
          id="si-notes"
          rows={4}
          value={draft.notes}
          onChange={(e) => set('notes', e.target.value)}
          placeholder="Visible to team only…"
          className="w-full rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-[var(--ollio-primary)]"
        />
      </section>

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button
          onClick={() => save.mutate()}
          disabled={!isDirty || save.isPending}
          loading={save.isPending}
        >
          Save
        </Button>
        {!isDirty && (
          <span className="text-xs text-muted">No changes</span>
        )}
      </div>
    </div>
  );
}
